import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TranslationService } from '@/layout/service/translation.service';
import { StatusBadgeComponent, status } from '@/shared/ui';
import { POWER_SERVICE } from 'src/api/power.service.interface';
import { OutletAction, OutletState, PowerDevice, PowerOutlet, hasFeature, isOutletOn } from 'src/api/power';

type TagSeverity = 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast';

/**
 * One PDU: header with totals and web UI link, and an outlet table with on / off / cycle
 * controls. Uses the POWER_SERVICE provided by the host page, so the same card works on the
 * processor dashboard and in the cloud (device page, room insights, linked device detail).
 */
@Component({
    selector: 'app-pdu-card',
    standalone: true,
    imports: [DecimalPipe, ButtonModule, ConfirmDialogModule, TagModule, TooltipModule, StatusBadgeComponent],
    providers: [ConfirmationService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <p-confirmdialog />
        <section class="pdu-card" [attr.aria-label]="device().name">
            <header class="pdu-header">
                <div class="pdu-title">
                    <i class="pdu-icon fa fa-plug-circle-bolt" aria-hidden="true"></i>
                    <div class="pdu-name">
                        <div class="pdu-name-row">
                            <h3 class="pdu-heading" [title]="device().name">{{ device().name }}</h3>
                            <app-status-badge [status]="onlineStatus()" size="sm" />
                        </div>
                        <div class="pdu-model">
                            {{ modelLine() }}
                            @if (device().roomName) {
                                · {{ device().roomName }}
                            }
                        </div>
                    </div>
                </div>
                <div class="pdu-summary">
                    @if (metered()) {
                        <div class="pdu-stat">
                            <span class="pdu-stat-label">{{ t()('power.totalPower') }}</span>
                            <span class="pdu-stat-value">{{ device().total?.watts ?? 0 | number: '1.0-0' }} W</span>
                        </div>
                        @if (energyKWh() != null) {
                            <div class="pdu-stat">
                                <span class="pdu-stat-label">{{ t()('power.energy') }}</span>
                                <span class="pdu-stat-value">{{ energyKWh() | number: '1.0-1' }} kWh</span>
                            </div>
                        }
                    }
                    <div class="pdu-stat">
                        <span class="pdu-stat-label">{{ t()('power.outletsOn') }}</span>
                        <span class="pdu-stat-value">{{ onCount() }} / {{ device().outlets.length }}</span>
                    </div>
                    @if (device().webUiUrl) {
                        <p-button [label]="t()('power.webUi')" icon="fa fa-arrow-up-right-from-square" size="small" [outlined]="true" (onClick)="openWebUi()" />
                    }
                </div>
            </header>

            @if (!controlEnabled() && switched()) {
                <div class="pdu-readonly" role="status">
                    <i class="fa fa-lock" aria-hidden="true"></i><span>{{ readOnlyReason() }}</span>
                </div>
            }

            <div class="table-scroll">
                <table class="pdu-table">
                    <thead>
                        <tr>
                            <th scope="col" class="w-12">#</th>
                            <th scope="col">{{ t()('power.outlet') }}</th>
                            <th scope="col">{{ t()('power.state') }}</th>
                            @if (outletMetered()) {
                                <th scope="col" class="num">{{ t()('power.watts') }}</th>
                                <th scope="col" class="num">{{ t()('power.amps') }}</th>
                            }
                            @if (switched()) {
                                <th scope="col" class="actions">{{ t()('power.control') }}</th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        @for (outlet of device().outlets; track outlet.id) {
                            <tr>
                                <td class="text-muted-color">{{ outlet.id }}</td>
                                <td>
                                    <span class="outlet-name">
                                        <span class="font-medium">{{ outlet.name }}</span>
                                        @if (outlet.locked) {
                                            <i class="fa fa-lock text-muted-color" [pTooltip]="outlet.lockReason || t()('power.locked')" [attr.aria-label]="t()('power.locked') + ': ' + (outlet.lockReason || '')"></i>
                                        }
                                    </span>
                                </td>
                                <td class="state">
                                    <p-tag [value]="stateLabel(outlet.state)" [severity]="stateSeverity(outlet.state)" [icon]="stateIcon(outlet.state)" />
                                </td>
                                @if (outletMetered()) {
                                    <td class="num">{{ outlet.metered && outlet.reading ? (outlet.reading.watts | number: '1.0-1') : '–' }}</td>
                                    <td class="num">{{ outlet.metered && outlet.reading?.amps != null ? (outlet.reading!.amps | number: '1.2-2') : '–' }}</td>
                                }
                                @if (switched()) {
                                    <td class="actions">
                                        <span class="actions-group">
                                            @if (outlet.switchable) {
                                                <p-button
                                                    [icon]="isOutletOn(outlet) ? 'fa fa-toggle-on' : 'fa fa-toggle-off'"
                                                    [text]="true"
                                                    size="small"
                                                    [pTooltip]="isOutletOn(outlet) ? t()('power.turnOff') : t()('power.turnOn')"
                                                    [ariaLabel]="(isOutletOn(outlet) ? t()('power.turnOff') : t()('power.turnOn')) + ' ' + outlet.name"
                                                    [disabled]="!canRun(outlet, isOutletOn(outlet) ? 'off' : 'on')"
                                                    [loading]="busy().has(outlet.id)"
                                                    (onClick)="request(outlet, isOutletOn(outlet) ? 'off' : 'on')"
                                                />
                                                <p-button
                                                    icon="fa fa-arrows-rotate"
                                                    [text]="true"
                                                    size="small"
                                                    [pTooltip]="t()('power.cycle')"
                                                    [ariaLabel]="t()('power.cycle') + ' ' + outlet.name"
                                                    [disabled]="!canRun(outlet, 'cycle')"
                                                    (onClick)="request(outlet, 'cycle')"
                                                />
                                            }
                                        </span>
                                    </td>
                                }
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
        </section>
    `,
    styles: [
        `
            .pdu-card {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }
            .pdu-header {
                display: flex;
                flex-wrap: wrap;
                gap: 1rem;
                align-items: center;
                justify-content: space-between;
            }
            .pdu-title {
                display: flex;
                align-items: flex-start;
                gap: 0.75rem;
                min-width: 0;
                flex: 1 1 14rem;
            }
            .pdu-icon {
                flex-shrink: 0;
                font-size: 1.25rem;
                line-height: 1.75rem;
            }
            .pdu-name {
                display: flex;
                flex-direction: column;
                gap: 0.125rem;
                min-width: 0;
            }
            .pdu-name-row {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                min-width: 0;
            }
            .pdu-heading {
                margin: 0;
                font-size: 1.125rem;
                font-weight: 600;
                line-height: 1.75rem;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .pdu-model {
                font-size: 0.875rem;
                color: var(--text-color-secondary);
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .pdu-summary {
                display: flex;
                flex-wrap: wrap;
                gap: 0.75rem 1.5rem;
                align-items: flex-end;
            }
            .pdu-stat {
                display: flex;
                flex-direction: column;
                line-height: 1.2;
            }
            .pdu-stat-label {
                font-size: 0.75rem;
                color: var(--text-color-secondary);
            }
            .pdu-stat-value {
                font-size: 1.125rem;
                font-weight: 600;
                font-variant-numeric: tabular-nums;
            }
            .pdu-readonly {
                display: flex;
                align-items: baseline;
                gap: 0.5rem;
                font-size: 0.875rem;
                color: var(--text-color-secondary);
            }
            .table-scroll {
                overflow-x: auto;
            }
            .pdu-table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.875rem;
            }
            .pdu-table th {
                text-align: left;
                font-weight: 600;
                color: var(--text-color-secondary);
                padding: 0.5rem 0.75rem;
                border-bottom: 1px solid var(--surface-border);
                white-space: nowrap;
            }
            .pdu-table td {
                padding: 0.5rem 0.75rem;
                border-bottom: 1px solid var(--surface-border);
                vertical-align: middle;
            }
            .pdu-table .num {
                text-align: right;
                font-variant-numeric: tabular-nums;
                white-space: nowrap;
            }
            .pdu-table th:first-child,
            .pdu-table td:first-child {
                padding-left: 0;
            }
            .pdu-table th:last-child,
            .pdu-table td:last-child {
                padding-right: 0;
            }
            .pdu-table tbody tr:last-child td {
                border-bottom: 0;
            }
            .outlet-name {
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
                white-space: nowrap;
            }
            .pdu-table .state {
                white-space: nowrap;
            }
            .pdu-table .actions {
                text-align: right;
                white-space: nowrap;
            }
            .actions-group {
                display: inline-flex;
                gap: 0.25rem;
                justify-content: flex-end;
            }
        `
    ]
})
export class PduCardComponent {
    private power = inject(POWER_SERVICE);
    private confirm = inject(ConfirmationService);
    private messages = inject(MessageService);
    readonly t = inject(TranslationService).t;
    readonly isOutletOn = isOutletOn;

    readonly device = input.required<PowerDevice>();
    readonly energyKWh = input<number | null>(null);
    /** Emitted with the outlet returned by a successful action, for the host to merge. */
    readonly outletChanged = output<{ key: string; outlet: PowerOutlet }>();

    readonly busy = signal<ReadonlySet<number>>(new Set());

    readonly switched = computed(() => hasFeature(this.device(), 'Switched'));
    readonly metered = computed(() => hasFeature(this.device(), 'OutletMetering') || hasFeature(this.device(), 'TotalMetering'));
    readonly outletMetered = computed(() => this.device().outlets.some((o) => o.metered));
    readonly onCount = computed(() => this.device().outlets.filter((o) => isOutletOn(o) || o.state === 'Cycling').length);
    readonly controlEnabled = computed(() => this.power.canControl() && this.device().online);
    readonly readOnlyReason = computed(() => (!this.power.canControl() ? this.t()('power.readOnlyTunnel') : this.t()('power.readOnlyOffline')));
    readonly modelLine = computed(() => [this.device().manufacturerName, this.device().modelName].filter(Boolean).join(' '));
    readonly onlineStatus = computed(() => (this.device().online ? status('online', this.t()('power.online')) : status('offline', this.t()('power.offline'))));

    canRun(outlet: PowerOutlet, action: OutletAction): boolean {
        if (!this.controlEnabled() || !outlet.switchable || this.busy().has(outlet.id) || outlet.state === 'Cycling') return false;
        return !(outlet.locked && action !== 'on');
    }

    request(outlet: PowerOutlet, action: OutletAction): void {
        if (!this.canRun(outlet, action)) return;
        if (action === 'on') {
            void this.run(outlet, action);
            return;
        }
        const key = action === 'cycle' ? 'power.confirmCycle' : 'power.confirmOff';
        this.confirm.confirm({
            header: this.t()(action === 'cycle' ? 'power.cycle' : 'power.turnOff'),
            message: this.t()(key).replace('{outlet}', outlet.name).replace('{device}', this.device().name),
            icon: 'fa fa-triangle-exclamation',
            acceptLabel: this.t()(action === 'cycle' ? 'power.cycle' : 'power.turnOff'),
            rejectLabel: this.t()('power.cancel'),
            acceptButtonProps: { severity: 'danger' },
            rejectButtonProps: { severity: 'secondary', outlined: true },
            accept: () => void this.run(outlet, action)
        });
    }

    openWebUi(): void {
        const url = this.device().webUiUrl;
        if (url && /^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener,noreferrer');
    }

    stateLabel(state: OutletState): string {
        return this.t()(`power.state.${state}`);
    }

    stateSeverity(state: OutletState): TagSeverity {
        switch (state) {
            case 'On':
            case 'AlwaysOn':
                return 'success';
            case 'Cycling':
                return 'info';
            case 'Off':
                return 'secondary';
            default:
                return 'warn';
        }
    }

    stateIcon(state: OutletState): string {
        switch (state) {
            case 'On':
                return 'fa fa-power-off';
            case 'AlwaysOn':
                return 'fa fa-bolt';
            case 'Cycling':
                return 'fa fa-arrows-rotate';
            case 'Off':
                return 'fa fa-circle';
            default:
                return 'fa fa-question';
        }
    }

    private async run(outlet: PowerOutlet, action: OutletAction): Promise<void> {
        this.setBusy(outlet.id, true);
        try {
            const result = await this.power.executeOutletAction(this.device(), outlet.id, action);
            if (result.outlet) this.outletChanged.emit({ key: this.device().key, outlet: result.outlet });
            if (!result.success) {
                this.messages.add({ severity: 'warn', summary: outlet.name, detail: result.message || this.t()('power.actionFailed') });
            }
        } catch (e) {
            this.messages.add({ severity: 'error', summary: outlet.name, detail: e instanceof Error ? e.message : this.t()('power.actionFailed') });
        } finally {
            this.setBusy(outlet.id, false);
        }
    }

    private setBusy(outletId: number, busy: boolean): void {
        this.busy.update((set) => {
            const next = new Set(set);
            if (busy) next.add(outletId);
            else next.delete(outletId);
            return next;
        });
    }
}
